import productsData from '~/data/index';

/**
 * Enriches a Shopify product with includes data from static product files.
 * This is a pure function with no reactive dependencies - safe to call anywhere.
 *
 * @param productId - The Shopify product GID (e.g., "gid://shopify/Product/123")
 * @param isDev - Whether we're in development mode
 * @returns The includes array from static data, or empty array if not found
 */
export function getProductIncludes(productId: string | undefined, isDev = false): any[] {
  if (!productId) return [];

  const productJson = Object.values(productsData).find((product: any) => {
    const id = isDev ? product.testShopifyId : product.prodShopifyId;
    return id === productId;
  });

  return productJson?.includes ?? [];
}

/**
 * Enriches a Shopify product variant with all necessary data for cart/checkout display.
 * This includes looking up the includes array from static data.
 *
 * @param fullProduct - The full product from Shopify collection
 * @param variantId - The variant ID being displayed
 * @param cartItem - The cart item (includes qty, id, etc)
 * @param isDev - Whether we're in development mode
 * @returns Enriched product variant with all display data
 */
export function enrichProductVariant(
  fullProduct: any,
  variantId: string,
  cartItem: any,
  isDev = false,
) {
  if (!fullProduct) {
    return {
      ...cartItem,
      productName: 'Product',
      includes: [],
      pricingDetails: '',
      pricingDescription: '',
      price: 0,
      oldPrice: null,
      productType: 'equipment',
    };
  }

  const productVariant = fullProduct.variants?.find((v: any) => v.id === variantId);
  const productName = fullProduct.variants?.length > 1
    ? `${fullProduct.title}, ${productVariant?.name}`
    : fullProduct.title;

  // Look up includes from static data (not from Shopify)
  const includes = getProductIncludes(fullProduct.id, isDev);

  return {
    ...cartItem,
    productName: productName || 'Product',
    includes,
    pricingDetails: fullProduct.description,
    pricingDescription: fullProduct.description,
    price: productVariant?.price ?? 0,
    oldPrice: productVariant?.compareAtPrice ?? null,
    productType: fullProduct.productType ?? 'equipment',
  };
}
